import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";

import type {NotificationItem, NotificationType} from "../types";

export type StoredVehicleNotificationPayload = {
  stored_vehicle_id?: number;
  property_id?: number;
  vehicle_id?: number;
  plate?: string;
  property_name?: string;
  make?: string;
  model?: string;
  tracking_started_at?: string;
  cite_eligible_at?: string;
};

export type BriefingBoardNotificationPayload = {
  briefing_board_id?: number;
  brief_snippet?: string;
  created_by?: string;
  property_name?: string;
};

export type ShiftNotificationPayload = {
  shift_id?: number;
  shift_break_id?: number;
  break_type?: string;
  property_name?: string;
};

export type PanicNotificationPayload = {
  panic_alert_id?: number;
  property_name?: string;
  severity_level?: string;
  alert_type?: string;
};

export type DirectMessageNotificationPayload = {
  direct_message_thread_id?: number;
  sender_name?: string;
  subject?: string;
};

export type NotificationCardDisplay = {
  headline: string;
  subline?: string | null;
  body?: string | null;
};

export type NotificationStatusBadge = {
  label: string;
  variant: "success" | "warning" | "outline";
};

function vehicleLabel(payload: Record<string, unknown>): string {
  const p = payload as StoredVehicleNotificationPayload;
  return [p.make, p.model].filter(Boolean).join(" ").trim();
}

function formatBreakType(breakType?: string): string | null {
  const value = breakType?.trim();
  if (!value) {
    return null;
  }
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function pickDetailBody(
  headline: string,
  subline: string | null | undefined,
  ...candidates: (string | null | undefined)[]
): string | null {
  const used = new Set(
    [headline, subline]
      .filter((value): value is string => Boolean(value?.trim()))
      .map((value) => value.trim().toLowerCase()),
  );

  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed && !used.has(trimmed.toLowerCase())) {
      return trimmed;
    }
  }

  return null;
}

function getShiftDefaultHeadline(type: NotificationType): string {
  if (type === "shift.break_alert") {
    return "Break due";
  }
  if (type === "shift.end_alert") {
    return "Shift ending";
  }
  return "Shift alert";
}

function parseDirectMessageTitle(title: string): {headline: string; subline: string | null} {
  const colonIndex = title.indexOf(":");
  if (colonIndex > 0 && colonIndex < title.length - 1) {
    const sender = title.slice(0, colonIndex).trim();
    const subject = title.slice(colonIndex + 1).trim();
    return {
      headline: subject || title,
      subline: sender || null,
    };
  }
  return {headline: title, subline: null};
}

function getStoredVehicleCardBody(item: NotificationItem): string | null {
  const vehicle = vehicleLabel(item.payload);
  const p = item.payload as StoredVehicleNotificationPayload;

  if (item.type === "stored_vehicle.tracking_active") {
    if (!p.tracking_started_at) {
      return vehicle || null;
    }
    const when = `${formatDate(p.tracking_started_at)} ${formatTime(p.tracking_started_at)}`;
    return vehicle ? `${vehicle} · started ${when}` : `Started ${when}`;
  }

  if (item.type === "stored_vehicle.cite_eligible") {
    if (!p.cite_eligible_at) {
      return vehicle || null;
    }
    const when = `${formatDate(p.cite_eligible_at)} ${formatTime(p.cite_eligible_at)}`;
    return vehicle ? `${vehicle} · eligible since ${when}` : `Eligible since ${when}`;
  }

  return vehicle || null;
}

function getStoredVehiclePlatePropertyTitle(item: NotificationItem): string {
  const p = item.payload as StoredVehicleNotificationPayload;
  const plate = p.plate?.trim() || "—";
  const propertyName = p.property_name?.trim();
  return propertyName ? `${plate} at ${propertyName}` : plate;
}

function getBriefingBoardCardDisplay(item: NotificationItem): NotificationCardDisplay {
  const p = item.payload as BriefingBoardNotificationPayload;
  const apiTitle = item.title?.trim();
  const snippet = p.brief_snippet?.trim();
  const createdBy = p.created_by?.trim();
  const propertyName = p.property_name?.trim();
  const message = item.message?.trim();

  const headline =
    apiTitle ||
    snippet ||
    (item.type === "briefing_board.ack_required"
      ? "Briefing acknowledgment required"
      : "New briefing");

  const subline =
    (createdBy ? `From ${createdBy}` : null) ||
    propertyName ||
    null;

  const body = pickDetailBody(headline, subline, message, snippet);

  return {headline, subline, body};
}

function getShiftCardDisplay(item: NotificationItem): NotificationCardDisplay {
  const p = item.payload as ShiftNotificationPayload;
  const headline = item.title?.trim() || getShiftDefaultHeadline(item.type);
  const subline = formatBreakType(p.break_type) || p.property_name?.trim() || null;
  const body = pickDetailBody(headline, subline, item.message?.trim());

  return {headline, subline, body};
}

function getDirectMessageCardDisplay(item: NotificationItem): NotificationCardDisplay {
  const p = item.payload as DirectMessageNotificationPayload;
  const apiTitle = item.title?.trim();
  const message = item.message?.trim();
  const senderFromPayload = p.sender_name?.trim();
  const subjectFromPayload = p.subject?.trim();

  if (apiTitle) {
    const parsed = parseDirectMessageTitle(apiTitle);
    return {
      headline: parsed.headline,
      subline: parsed.subline || senderFromPayload || null,
      body: pickDetailBody(parsed.headline, parsed.subline || senderFromPayload, message),
    };
  }

  const headline = subjectFromPayload || "Direct message";
  const subline = senderFromPayload || null;

  return {
    headline,
    subline,
    body: pickDetailBody(headline, subline, message),
  };
}

/** Card + toast layout — headline/subline/body per topic. */
export function getNotificationCardDisplay(item: NotificationItem): NotificationCardDisplay {
  if (item.topic === "stored_vehicle") {
    const p = item.payload as StoredVehicleNotificationPayload;
    return {
      headline: p.plate?.trim() || "—",
      subline: p.property_name?.trim() || "—",
      body: getStoredVehicleCardBody(item),
    };
  }

  if (item.topic === "briefing_board") {
    return getBriefingBoardCardDisplay(item);
  }

  if (item.topic === "shift") {
    return getShiftCardDisplay(item);
  }

  if (item.topic === "direct_message") {
    return getDirectMessageCardDisplay(item);
  }

  if (item.topic === "panic") {
    const payload = item.payload as PanicNotificationPayload;
    return {
      headline: item.title?.trim() || "PANIC ALERT",
      subline: payload.property_name ?? null,
      body: item.message?.trim() || payload.severity_level || payload.alert_type || null,
    };
  }

  const headline = item.title?.trim() || "Notification";
  const body = pickDetailBody(headline, null, item.message?.trim());

  return {headline, body};
}

export function getNotificationOneLineTitle(item: NotificationItem): string {
  const {headline, subline} = getNotificationCardDisplay(item);

  if (!subline) {
    return headline;
  }

  if (item.topic === "stored_vehicle") {
    return `${headline} at ${subline}`;
  }

  if (item.topic === "direct_message") {
    return `${subline}: ${headline}`;
  }

  return `${headline} · ${subline}`;
}

export function getStoredVehicleNotificationTitle(item: NotificationItem): string {
  return getStoredVehiclePlatePropertyTitle(item);
}

export function getStoredVehicleNotificationBody(item: NotificationItem): string | null {
  return getStoredVehicleCardBody(item);
}

export function getBriefingBoardNotificationTitle(item: NotificationItem): string {
  return getBriefingBoardCardDisplay(item).headline;
}

export function getBriefingBoardNotificationBody(item: NotificationItem): string | null {
  const {subline, body} = getBriefingBoardCardDisplay(item);
  return body ?? subline ?? null;
}

export function getNotificationDisplayTitle(item: NotificationItem): string {
  return getNotificationOneLineTitle(item);
}

export function getNotificationDisplayBody(item: NotificationItem): string | null {
  const {body} = getNotificationCardDisplay(item);
  return body ?? null;
}

export function getNotificationSearchText(item: NotificationItem): string {
  const {headline, subline, body} = getNotificationCardDisplay(item);
  return [headline, subline, body].filter(Boolean).join(" ");
}

export function getNotificationStatusBadge(item: NotificationItem): NotificationStatusBadge | null {
  if (item.topic === "stored_vehicle") {
    if (item.type === "stored_vehicle.tracking_active") {
      return {label: "Tracking", variant: "success"};
    }
    if (item.type === "stored_vehicle.cite_eligible") {
      return {label: "Eligible to cite", variant: "warning"};
    }
    return null;
  }

  if (item.topic === "shift") {
    if (item.type === "shift.break_alert") {
      return {label: "Break due", variant: "warning"};
    }
    if (item.type === "shift.end_alert") {
      return {label: "Shift ending", variant: "warning"};
    }
    return null;
  }

  if (item.topic === "briefing_board") {
    if (item.type === "briefing_board.ack_required") {
      return {label: "Ack required", variant: "warning"};
    }
    if (item.type === "briefing_board.created") {
      return {label: "New briefing", variant: "success"};
    }
    return null;
  }

  if (item.topic === "direct_message" && item.type === "direct_message.post") {
    return {label: "New message", variant: "outline"};
  }

  return null;
}

/** @deprecated Use getNotificationStatusBadge */
export function getStoredVehicleStatusBadge(
  item: NotificationItem,
): NotificationStatusBadge | null {
  if (item.topic !== "stored_vehicle") {
    return null;
  }
  return getNotificationStatusBadge(item);
}

/** @deprecated Use getNotificationStatusBadge */
export function getShiftStatusBadge(item: NotificationItem): NotificationStatusBadge | null {
  if (item.topic !== "shift") {
    return null;
  }
  return getNotificationStatusBadge(item);
}
